import React, { Dispatch, SetStateAction } from 'react';
import { CreditCard, SavedCreditCard, Title } from '@components';
import { Typography, Accordion, AccordionContent, AccordionHeader, AccordionItem, Input } from 'gd-design-library';
import classnames from 'classnames/bind';
import styles from './PaymentMethodSelector.module.scss';
import { ADYEN_ERROR_GENERAL_NOTIFICATION } from '@constants';
import { Price } from '@services';
import creditCardIcon from '../../assets/images/credit-card.svg';
import paypalIcon from '../../assets/images/paypal.svg';
import { CreatePaymentWidgetParams, MountableElement, PAYMENT_METHODS, StoredPaymentMethodData } from 'integrations/payment';
import { ADYEN_ERROR_PAYPAL_NOTIFICATION } from '@constants/adyen';

interface PaymentMethodSelectorProps {
  paymentMethod: PAYMENT_METHODS;
  setPaymentMethod: Dispatch<SetStateAction<PAYMENT_METHODS>>;
  availablePaymentMethods: string[] | null;
  storedPaymentMethods: StoredPaymentMethodData[] | null;
  createPaymentWidget: (params: CreatePaymentWidgetParams) => Promise<MountableElement>;
  setIsPaymentDataValid: Dispatch<SetStateAction<boolean>>;
  setIsSavedPaymentDataValid: Dispatch<SetStateAction<boolean>>;
  paymentError: string;
  amount?: Price;
}

const cx = classnames.bind(styles);

const CN = 'payment-method-selector';
const paymentErrorClassName = `${CN}__payment-error`;
const titleClassName = `${CN}__title`;
const accordionClassName = 'accordion';
const accordionOpenedClassName = 'accordion--opened';
const accordionSummaryClassName = 'accordion-summary';
const accordionSummaryWrapperClassName = `${accordionSummaryClassName}__wrapper`;
const accordionSummaryTextClassName = `${accordionSummaryClassName}__text`;
const accordionSummaryIconClassName = `${accordionSummaryClassName}__icon`;
const accordionRadioButtonClassName = `${accordionSummaryClassName}__radio-button`;

export const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  setIsPaymentDataValid,
  paymentError,
  paymentMethod,
  setPaymentMethod,
  createPaymentWidget,
  storedPaymentMethods,
  availablePaymentMethods,
  setIsSavedPaymentDataValid
}) => {
  const accordionChange = (value: PAYMENT_METHODS[]): void => {
    if (paymentMethod !== value[0]) {
      setPaymentMethod(value[0]);
    }
  };

  return <div className={ cx(CN) }>
    { paymentError && <div className={ cx(paymentErrorClassName) }>
      <Typography variant='h6'>{ paymentError }</Typography>
      <Typography>
        { paymentMethod === PAYMENT_METHODS.PayPal ? ADYEN_ERROR_PAYPAL_NOTIFICATION : ADYEN_ERROR_GENERAL_NOTIFICATION }
      </Typography>
    </div> }
    <Title className={ cx(titleClassName) }>Payment details</Title>
    <Accordion onChange={ accordionChange } value={ [ paymentMethod ] }>
      {!!storedPaymentMethods?.length && <AccordionItem
        className={ cx(accordionClassName, paymentMethod === PAYMENT_METHODS.SavedCard && accordionOpenedClassName) }
        id={ PAYMENT_METHODS.SavedCard }
      >
        <AccordionHeader
          className={ cx(accordionSummaryWrapperClassName) }
          expandIcon={ <Input
            variant='radio'
            checked={ paymentMethod === PAYMENT_METHODS.SavedCard }
            readOnly
            className={ cx(accordionRadioButtonClassName) }
          /> }
        >
          <div className={ cx(accordionSummaryClassName) }>
            <img className={ cx(accordionSummaryIconClassName) } src={ creditCardIcon.src }/>
            <span className={ cx(accordionSummaryTextClassName) }>Saved Card</span>
          </div>
        </AccordionHeader>
        <AccordionContent>
          <SavedCreditCard
            storedPaymentMethods={ storedPaymentMethods }
            createPaymentWidget={ createPaymentWidget }
            setIsSavedPaymentDataValid={ setIsSavedPaymentDataValid }
          />
        </AccordionContent>
      </AccordionItem>}
      {availablePaymentMethods?.some(method => method === 'scheme') && <AccordionItem
        className={ cx(accordionClassName, paymentMethod === PAYMENT_METHODS.Card && accordionOpenedClassName) }
        id={ PAYMENT_METHODS.Card }
      >
        <AccordionHeader
          className={ cx(accordionSummaryWrapperClassName) }
          expandIcon={ <Input
            variant='radio'
            checked={ paymentMethod === PAYMENT_METHODS.Card }
            readOnly
            className={ cx(accordionRadioButtonClassName) }
          /> }
        >
          <div className={ cx(accordionSummaryClassName) }>
            <img className={ cx(accordionSummaryIconClassName) } src={ creditCardIcon.src }/>
            <span className={ cx(accordionSummaryTextClassName) }>Credit or Debit Card</span>
          </div>
        </AccordionHeader>
        <AccordionContent>
          <CreditCard
            createPaymentWidget={ createPaymentWidget }
            setIsPaymentDataValid={ setIsPaymentDataValid }
          />
        </AccordionContent>
      </AccordionItem>}
      {availablePaymentMethods?.some(method => method === 'paypal') && <AccordionItem
        className={ cx(accordionClassName, paymentMethod === PAYMENT_METHODS.PayPal && accordionOpenedClassName) }
        id={ PAYMENT_METHODS.PayPal }
      >
        <AccordionHeader
          className={ cx(accordionSummaryWrapperClassName) }
          expandIcon={ <Input
            variant='radio'
            checked={ paymentMethod === PAYMENT_METHODS.PayPal }
            readOnly
            className={ cx(accordionRadioButtonClassName) }
          /> }
        >
          <div className={ cx(accordionSummaryClassName) }>
            <img className={ cx(accordionSummaryIconClassName) } src={ paypalIcon.src }/>
            <span className={ cx(accordionSummaryTextClassName) }>PayPal</span>
          </div>
        </AccordionHeader>
      </AccordionItem>}
    </Accordion>
  </div>;
};
